import { v4 as uuidv4 } from 'uuid'

export const createToastUtilObject = (handleToastCreated = (toast) => toast) => {
  return {
    success: (message, options) => handleToastCreated(createSuccessToast(message, options)),
    error: (message, options) => handleToastCreated(createErrorToast(message, options)),
    info: (message, options) => handleToastCreated(createInfoToast(message, options)),
    warning: (message, options) => handleToastCreated(createWarningToast(message, options)),
    created: (objectName, options) => createCreatedToast(objectName, options, handleToastCreated),
    updated: (objectName, options) => createUpdatedToast(objectName, options, handleToastCreated),
    sent: (objectName, options) => createSentToast(objectName, options, handleToastCreated),
    archived: (objectName, options) => createArchivedToast(objectName, options, handleToastCreated),
    unarchived: (objectName, options) => createUnarchivedToast(objectName, options, handleToastCreated),
    removed: (objectName, options) => createRemovedToast(objectName, options, handleToastCreated),
    added: (objectName, options) => createAddedToast(objectName, options, handleToastCreated),
    apiErrors: (errors) => createApiErrorsToast(errors, handleToastCreated),
  }
}

export const addToastToMapWithUniqueId = (toast, toastMap) => {
  toastMap.set(uuidv4(), toast)
}

const createToast = ({ message, type = 'info', messageIcon, options }) => {
  const toastArgs = {
    message,
    type,
    options: {
      messageIcon: messageIcon,
      ...options,
    },
  }
  
  return formatToastArgs(toastArgs)
}

function formatToastArgs(data) {
  const toast = {}
  const { message, type, options } = data

  toast.messageText = message
  toast.type = type

  if (!options)
    return toast

  toast.action = options.action || function () { }
  toast.actionButtonEmphasis = options.actionButtonEmphasis || ''
  toast.actionIcon = options.actionIcon || ''
  toast.actionText = options.actionText || ''
  toast.duration = options.duration || 'medium'
  toast.messageIcon = options.messageIcon || ''

  return toast
}

const createSuccessToast = (message, options) => {
  return createToast({ message, type: 'success', messageIcon: 'far fa-check', options })
}

const createErrorToast = (message, options) => {
  return createToast({ message, type: 'error', messageIcon: 'fa-regular fa-circle-exclamation', options })
}

const createInfoToast = (message, options) => {
  return createToast({ message, type: 'info', options })
}

const createWarningToast = (message, options) => {
  return createToast({ message, type: 'warning', options })
}

const createCreatedToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createSuccessToast(`${objectName} created`, options)),
    error: () => handleToastCreated(createErrorToast(`There was an issue creating ${objectName}`, options)),
  }
}

const createUpdatedToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createSuccessToast(`${objectName} updated`, options)),
    error: () => handleToastCreated(createErrorToast(`There was an issue updating ${objectName}`, options)),
  }
}

const createSentToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createSuccessToast(`${objectName} sent`, options)),
    error: () => handleToastCreated(createErrorToast(`There was an issue sending this ${objectName}`, options)),
  }
}

const createArchivedToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createInfoToast(`${objectName} archived`, { messageIcon: 'fa-regular fa-archive', ...options })),
    error: () => handleToastCreated(createErrorToast(`There was an issue archiving ${objectName}`, options)),
  }
}

const createUnarchivedToast = (objectName, options, handleToastCreated) => {
  return {
    success:
      () => handleToastCreated(createInfoToast(`${objectName} unarchived`, { messageIcon: 'fa-regular fa-archive', ...options })),
    error: () => handleToastCreated(createErrorToast(`There was an issue unarchiving ${objectName}`, options)),
  }
}

const createRemovedToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createInfoToast(`${objectName} removed`, { messageIcon: 'fa-regular fa-trash-can', ...options })),
    error: () => handleToastCreated(createErrorToast(`There was an issue removing ${objectName}`, options)),
  }
}

const createAddedToast = (objectName, options, handleToastCreated) => {
  return {
    success: () => handleToastCreated(createSuccessToast(`${objectName} added`, options)),
    error: () => handleToastCreated(createErrorToast(`There was an issue adding ${objectName}`, options)),
  }
}

const createApiErrorsToast = (errors, handleToastCreated) => {
  errors.forEach(apiError => {
    handleToastCreated(createErrorToast(apiError.detail, { messageIcon: 'fa-regular fa-circle-exclamation' }))
  })
}
